import React, { useMemo, useState } from "react";

import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Checkbox from "@cloudscape-design/components/checkbox";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Container from "@cloudscape-design/components/container";
import DatePicker from "@cloudscape-design/components/date-picker";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Link from "@cloudscape-design/components/link";
import ProgressBar from "@cloudscape-design/components/progress-bar";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";

import { useRecoilValue } from "recoil";
import { userStore } from "../stores/access";

const BONUS_TYPES = [
	{ value: 8, label: "Wheel", unit: ["spin", "spins"] },
	{ value: 24, label: "Rain", unit: ["rain", "rains"] },
	{ value: 7, label: "Promo codes", unit: ["activation", "activations"] },
	{ value: 181, label: "Starting deposit ladder", unit: ["step", "steps"] },
];

const DEFAULT_THRESHOLDS = {
	activity: 40,
	withdrawalsToDeposits: 70,
	bonusesToDeposits: 30,
};

const PERCENT_OPTIONS = Array.from({ length: 101 }, (_, value) => ({
	value: String(value),
	label: `${value}%`,
}));

function formatDate(date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
}

function getDefaultPeriod() {
	const dateTo = new Date();
	const dateFrom = new Date();
	dateFrom.setDate(dateFrom.getDate() - 30);

	return {
		dateFrom: formatDate(dateFrom),
		dateTo: formatDate(dateTo),
	};
}

function formatGeo(country, region) {
	const parts = [country, region].filter(Boolean);
	return parts.length ? parts.join(", ") : "—";
}

function normalizeResponse(response) {
	const referrer = response?.referrer || {};
	const financial = response?.financial || {};

	return {
		referrer: {
			uid: referrer.uid,
			balance: referrer.balance,
			currency: referrer.currency,
			totalReferrals: referrer.totalReferrals,
			referralsWithDeposits: referrer.referralsWithDeposits,
		},
		financial: {
			referralsWithDeposits: financial.referralsWithDeposits,
			referralsWithLogins: financial.referralsWithLogins,
			loginDaysCount: financial.loginDaysCount,
			activityPercent: financial.activityPercent ?? null,
			deposits: financial.depositsTotal,
			depositsCount: financial.depositsCount,
			withdrawals: financial.withdrawalsTotal,
			withdrawalsCount: financial.withdrawalsCount,
			referralsWithWithdrawals: financial.referralsWithWithdrawals,
			withdrawalsToDepositsPercent: financial.withdrawalsToDepositsPercent ?? null,
			bonuses: financial.bonusesTotal,
			bonusesCount: financial.bonusesCount,
			bonusesByType: Array.isArray(financial.bonusesByType) ? financial.bonusesByType : [],
			bonusesToDepositsPercent: financial.bonusesToDepositsPercent ?? null,
		},
		ipMatches: (Array.isArray(response?.ipMatches) ? response.ipMatches : []).map((item, index) => ({
			id: `ip-${item.uid}-${item.ip}-${index}`,
			uid: item.uid,
			value: item.ip,
			geo: formatGeo(item.country, item.region),
			deposits: item.deposits,
			depositShare: item.depositSharePercent ?? null,
		})),
		walletMatches: (Array.isArray(response?.walletMatches) ? response.walletMatches : []).map((item, index) => ({
			id: `wallet-${item.uid}-${item.wallet}-${index}`,
			uid: item.uid,
			value: item.wallet,
			deposits: item.deposits,
			depositShare: item.depositSharePercent ?? null,
		})),
	};
}

function formatNumber(value, maximumFractionDigits = 2) {
	if (value === null || typeof value === "undefined" || !Number.isFinite(Number(value))) return "—";

	return new Intl.NumberFormat("ru-RU", { maximumFractionDigits }).format(Number(value));
}

function formatAmount(value, currency) {
	const formatted = formatNumber(value);
	return currency && formatted !== "—" ? `${formatted} ${currency}` : formatted;
}

function formatPercent(value) {
	return value === null ? "—" : `${formatNumber(value)}%`;
}

const METRIC_STATUSES = {
	ok: { type: "success", label: "OK" },
	failed: { type: "warning", label: "Outside threshold" },
	noData: { type: "stopped", label: "No data" },
};

function getMetricStatus(ok) {
	if (ok === null) return METRIC_STATUSES.noData;
	return ok ? METRIC_STATUSES.ok : METRIC_STATUSES.failed;
}

function pluralize(count, singular, plural) {
	return `${count} ${count === 1 ? singular : plural}`;
}

function renderValue(label, value, description) {
	return (
		<div>
			<Box variant="awsui-key-label">{label}</Box>
			<Box fontSize="heading-l" fontWeight="bold">
				{value}
			</Box>
			{description ? (
				<Box variant="small" color="text-body-secondary">
					{description}
				</Box>
			) : null}
		</div>
	);
}

function renderMetricCard(metric) {
	const status = getMetricStatus(metric.ok);

	return (
		<Container key={metric.id} fitHeight header={<Header variant="h3" description={metric.description}>{metric.metric}</Header>}>
			<SpaceBetween size="l">
				<SpaceBetween size="xxs">
					<Box fontSize="display-l" fontWeight="bold">
						{formatPercent(metric.value)}
					</Box>
					<SpaceBetween direction="horizontal" size="xs">
						<StatusIndicator type={status.type}>{status.label}</StatusIndicator>
						<Box color="text-body-secondary">
							· target {metric.operator === "minimum" ? "≥" : "≤"} {metric.threshold}%
						</Box>
					</SpaceBetween>
				</SpaceBetween>
				<ColumnLayout columns={2} variant="text-grid">
					{metric.parts.map((part) => (
						<div key={part.label}>
							<Box variant="awsui-key-label">{part.label}</Box>
							<div>{part.value}</div>
							{part.hint ? (
								<Box variant="small" color="text-body-secondary">
									{part.hint}
								</Box>
							) : null}
						</div>
					))}
				</ColumnLayout>
			</SpaceBetween>
		</Container>
	);
}

function renderShare(value) {
	if (value === null || typeof value === "undefined") return "—";

	return <ProgressBar value={Math.min(Number(value.toFixed(1)), 100)} />;
}

function renderEmptyMatches(text) {
	return (
		<Box textAlign="center" padding="m">
			<StatusIndicator type="success">{text}</StatusIndicator>
		</Box>
	);
}

function ReferralCheck({ Interface }) {
	const user = useRecoilValue(userStore);
	const defaultPeriod = useMemo(() => getDefaultPeriod(), []);
	const projectOptions = useMemo(() => {
		const projects = [...(Array.isArray(user.brandsList) ? user.brandsList : [])];
		if (user.brand && !projects.some((project) => String(project.id) === String(user.brand.id))) projects.unshift(user.brand);

		return projects.map((project) => ({
			value: String(project.id),
			label: project.name,
		}));
	}, [user.brand, user.brandsList]);
	const [selectedProject, setSelectedProject] = useState(() => {
		if (user.brand) return { value: String(user.brand.id), label: user.brand.name };
		return null;
	});
	const [uid, setUid] = useState("");
	const [dateFrom, setDateFrom] = useState(defaultPeriod.dateFrom);
	const [dateTo, setDateTo] = useState(defaultPeriod.dateTo);
	const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
	const [selectedBonusTypes, setSelectedBonusTypes] = useState(BONUS_TYPES.map((option) => option.value));
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [result, setResult] = useState(null);

	const uidError = uid.length && (!/^\d+$/.test(uid) || !Number.isSafeInteger(Number(uid)) || Number(uid) <= 0) ? "Enter a positive integer UID" : "";
	const dateError = dateFrom && dateTo && dateFrom > dateTo ? "Date From cannot be later than Date To" : "";
	const formError = !selectedProject ? "Select a project" : !uid.length ? "Enter a UID" : uidError || !dateFrom || !dateTo ? "Select a complete analysis period" : dateError || !selectedBonusTypes.length ? "Select at least one bonus type" : "";

	const clearResult = () => {
		setResult(null);
		setError("");
	};

	const updateThreshold = (key, value) => {
		setThresholds((current) => ({ ...current, [key]: Number(value) }));
		clearResult();
	};

	const toggleBonusType = (value, checked) => {
		setSelectedBonusTypes((current) => (checked ? [...current, value] : current.filter((item) => item !== value)));
		clearResult();
	};

	const runCheck = async () => {
		if (formError) {
			setError(formError);
			return;
		}

		const payload = {
			project: /^\d+$/.test(selectedProject.value) ? Number(selectedProject.value) : selectedProject.value,
			uid: Number(uid),
			dateFrom,
			dateTo,
			bonusTypes: selectedBonusTypes,
		};

		setLoading(true);
		setError("");
		setResult(null);

		try {
			const response = await Interface.loadReferralCheck(payload);
			setResult({
				...normalizeResponse(response),
				thresholds: { ...thresholds },
				project: payload.project,
				projectName: selectedProject.label,
				dateFrom,
				dateTo,
			});
		} catch (requestError) {
			setError(requestError?.text || requestError?.message || "Unable to complete the referral check");
		} finally {
			setLoading(false);
		}
	};

	const financialRows = useMemo(() => {
		if (!result) return [];

		const financial = result.financial;
		const depositsPart = {
			label: "Deposits",
			value: formatAmount(financial.deposits, result.referrer.currency),
			hint: `${pluralize(financial.depositsCount ?? 0, "deposit", "deposits")} by ${pluralize(financial.referralsWithDeposits ?? 0, "referral", "referrals")}`,
		};

		return [
			{
				id: "activity",
				metric: "Activity",
				description: "Deposits in the period / days with at least one login, counted per referral",
				value: financial.activityPercent,
				parts: [
					{ label: "Deposits", value: formatNumber(financial.depositsCount, 0), hint: `by ${pluralize(financial.referralsWithDeposits ?? 0, "referral", "referrals")}` },
					{ label: "Login days", value: formatNumber(financial.loginDaysCount, 0), hint: financial.referralsWithLogins == null ? null : `by ${pluralize(financial.referralsWithLogins, "referral", "referrals")}` },
				],
				threshold: result.thresholds.activity,
				operator: "minimum",
			},
			{
				id: "withdrawals",
				metric: "Withdrawals / Deposits",
				description: "Referral withdrawals divided by referral deposits",
				value: financial.withdrawalsToDepositsPercent,
				parts: [
					{
						label: "Withdrawals",
						value: formatAmount(financial.withdrawals, result.referrer.currency),
						hint: `${pluralize(financial.withdrawalsCount ?? 0, "withdrawal", "withdrawals")} by ${pluralize(financial.referralsWithWithdrawals ?? 0, "referral", "referrals")}`,
					},
					depositsPart,
				],
				threshold: result.thresholds.withdrawalsToDeposits,
				operator: "maximum",
			},
			{
				id: "bonuses",
				metric: "Bonuses / Deposits",
				description: "Selected referral bonuses divided by referral deposits",
				value: financial.bonusesToDepositsPercent,
				parts: [
					{ label: "Bonuses", value: formatAmount(financial.bonuses, result.referrer.currency), hint: pluralize(financial.bonusesCount ?? 0, "accrual", "accruals") },
					depositsPart,
				],
				threshold: result.thresholds.bonusesToDeposits,
				operator: "maximum",
			},
		].map((metric) => ({
			...metric,
			ok: metric.value === null ? null : metric.operator === "minimum" ? metric.value >= metric.threshold : metric.value <= metric.threshold,
		}));
	}, [result]);

	const uidLink = (value) => (
		<Link
			variant="normal"
			onFollow={() => {
				Interface.loadInfo(value, result?.project);
			}}>
			{value}
		</Link>
	);

	const failedChecks = financialRows.filter((metric) => metric.ok === false).length;
	const fraudLinks = result ? result.ipMatches.length + result.walletMatches.length : 0;
	const hasIssues = failedChecks > 0 || fraudLinks > 0;
	const settingsSummary = `Activity ≥ ${thresholds.activity}% · Withdrawals ≤ ${thresholds.withdrawalsToDeposits}% · Bonuses ≤ ${thresholds.bonusesToDeposits}% · ${pluralize(selectedBonusTypes.length, "bonus type", "bonus types")}`;

	return (
		<SpaceBetween size="l">
			<Container
				header={
					<Header
						variant="h1"
						description="Check a referrer's financial metrics and direct fraud links with their referrals for the selected period."
						actions={
							<Button variant="primary" iconName="search" loading={loading} disabled={loading || Boolean(formError)} onClick={runCheck}>
								Check
							</Button>
						}>
						Referral Check
					</Header>
				}>
				<SpaceBetween size="l">
					<Grid gridDefinition={[{ colspan: { default: 12, s: 4 } }, { colspan: { default: 12, s: 3 } }, { colspan: { default: 12, s: 5 } }]}>
						<FormField label="Project" errorText={!selectedProject && error ? "Select a project" : ""} stretch>
							<Select
								filteringType="auto"
								disabled={loading}
								selectedOption={selectedProject}
								options={projectOptions}
								placeholder="Select a project"
								onChange={({ detail }) => {
									setSelectedProject(detail.selectedOption);
									clearResult();
								}}
							/>
						</FormField>
						<FormField label="UID" errorText={uidError} stretch>
							<Input
								disabled={loading}
								type="number"
								inputMode="numeric"
								value={uid}
								placeholder="Enter UID"
								onChange={({ detail }) => {
									setUid(detail.value);
									clearResult();
								}}
								onKeyDown={({ detail }) => {
									if (detail.key === "Enter" && !formError && !loading) runCheck();
								}}
							/>
						</FormField>
						<FormField label="Analysis period" errorText={dateError} stretch>
							<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
								<DatePicker
									disabled={loading}
									value={dateFrom}
									placeholder="From"
									onChange={({ detail }) => {
										setDateFrom(detail.value);
										clearResult();
									}}
								/>
								<DatePicker
									disabled={loading}
									value={dateTo}
									placeholder="To"
									onChange={({ detail }) => {
										setDateTo(detail.value);
										clearResult();
									}}
								/>
							</Grid>
						</FormField>
					</Grid>

					<ExpandableSection variant="footer" headerText="Check settings" headerDescription={settingsSummary}>
						<SpaceBetween size="l">
							<ColumnLayout columns={3}>
								<FormField label="Minimum activity" description="OK when the result is greater than or equal to this value.">
									<Select
										disabled={loading}
										selectedOption={PERCENT_OPTIONS[thresholds.activity]}
										options={PERCENT_OPTIONS}
										onChange={({ detail }) => updateThreshold("activity", detail.selectedOption.value)}
									/>
								</FormField>
								<FormField label="Maximum withdrawals / deposits" description="OK when the result is less than or equal to this value.">
									<Select
										disabled={loading}
										selectedOption={PERCENT_OPTIONS[thresholds.withdrawalsToDeposits]}
										options={PERCENT_OPTIONS}
										onChange={({ detail }) => updateThreshold("withdrawalsToDeposits", detail.selectedOption.value)}
									/>
								</FormField>
								<FormField label="Maximum bonuses / deposits" description="OK when the result is less than or equal to this value.">
									<Select
										disabled={loading}
										selectedOption={PERCENT_OPTIONS[thresholds.bonusesToDeposits]}
										options={PERCENT_OPTIONS}
										onChange={({ detail }) => updateThreshold("bonusesToDeposits", detail.selectedOption.value)}
									/>
								</FormField>
							</ColumnLayout>

							<FormField label="Included bonus types" description="Only selected bonus types are included in the Bonuses / Deposits calculation." errorText={!selectedBonusTypes.length ? "Select at least one bonus type" : ""}>
								<ColumnLayout columns={4}>
									{BONUS_TYPES.map((option) => (
										<Checkbox key={option.value} disabled={loading} checked={selectedBonusTypes.includes(option.value)} onChange={({ detail }) => toggleBonusType(option.value, detail.checked)}>
											{option.label}
										</Checkbox>
									))}
								</ColumnLayout>
							</FormField>
						</SpaceBetween>
					</ExpandableSection>
				</SpaceBetween>
			</Container>

			{error ? (
				<Alert type="error" header="Unable to run referral check">
					{error}
				</Alert>
			) : null}

			{result ? (
				<>
					<Alert type={hasIssues ? "warning" : "success"} header={hasIssues ? "Review required" : "No issues found"}>
						{hasIssues
							? `${pluralize(failedChecks, "financial check", "financial checks")} outside threshold, ${pluralize(fraudLinks, "direct fraud link", "direct fraud links")} found.`
							: "All financial checks are within thresholds and no direct fraud links were found."}
					</Alert>

					<Container header={<Header variant="h2" description={`${result.projectName} · ${result.dateFrom} – ${result.dateTo}`}>Referrer overview</Header>}>
						<ColumnLayout columns={4} variant="text-grid">
							{renderValue("UID", uidLink(result.referrer.uid))}
							{renderValue("Balance", formatAmount(result.referrer.balance, result.referrer.currency))}
							{renderValue("Total referrals", formatNumber(result.referrer.totalReferrals, 0), "all time")}
							{renderValue(
								"Referrals with deposits",
								formatNumber(result.referrer.referralsWithDeposits, 0),
								result.referrer.totalReferrals > 0 ? `${formatNumber((result.referrer.referralsWithDeposits / result.referrer.totalReferrals) * 100)}% of all referrals, in the selected period` : null,
							)}
						</ColumnLayout>
					</Container>

					<SpaceBetween size="s">
						<Header variant="h2" description="Boundary values are considered OK. Values outside the thresholds are highlighted.">
							Financial check
						</Header>
						<ColumnLayout columns={3}>{financialRows.map(renderMetricCard)}</ColumnLayout>
						{result.financial.bonusesByType.length ? (
							<Container header={<Header variant="h3" description="Referral bonuses included in the Bonuses / Deposits calculation.">Bonuses by type</Header>}>
								<ColumnLayout columns={result.financial.bonusesByType.length} variant="text-grid">
									{result.financial.bonusesByType.map((bonus) => (
										<div key={bonus.type}>
											{(() => {
												const bonusType = BONUS_TYPES.find((option) => option.value === bonus.type);
												const unit = bonusType?.unit || ["accrual", "accruals"];

												return renderValue(bonusType?.label || String(bonus.type), formatAmount(bonus.total, result.referrer.currency), pluralize(bonus.count ?? 0, unit[0], unit[1]));
											})()}
										</div>
									))}
								</ColumnLayout>
							</Container>
						) : null}
					</SpaceBetween>

					<SpaceBetween size="s">
						<Header variant="h2" description="Direct links between the UID and referrals who deposited or logged in during the selected period. Links between referrals are not analyzed.">
							Fraud signals
						</Header>
						<Tabs
							variant="container"
							tabs={[
								{
									id: "ip",
									label: `Matching IP addresses (${result.ipMatches.length})`,
									content: (
										<Table
											variant="embedded"
											wrapLines={true}
											trackBy="id"
											columnDefinitions={[
												{ id: "uid", header: "Referral UID", cell: (item) => uidLink(item.uid) },
												{ id: "ip", header: "Matching IP", cell: (item) => <Box variant="code">{item.value}</Box> },
												{ id: "geo", header: "GEO", cell: (item) => item.geo },
												{ id: "deposits", header: "Deposits", cell: (item) => formatAmount(item.deposits, result.referrer.currency) },
												{ id: "share", header: "Share of referral deposits", cell: (item) => renderShare(item.depositShare), minWidth: 200 },
											]}
											items={result.ipMatches}
											empty={renderEmptyMatches("No direct IP matches found")}
										/>
									),
								},
								{
									id: "wallet",
									label: `Matching withdrawal wallets (${result.walletMatches.length})`,
									content: (
										<Table
											variant="embedded"
											wrapLines={true}
											trackBy="id"
											columnDefinitions={[
												{ id: "uid", header: "Referral UID", cell: (item) => uidLink(item.uid) },
												{ id: "wallet", header: "Matching wallet", cell: (item) => <Box variant="code">{item.value}</Box> },
												{ id: "deposits", header: "Deposits", cell: (item) => formatAmount(item.deposits, result.referrer.currency) },
												{ id: "share", header: "Share of referral deposits", cell: (item) => renderShare(item.depositShare), minWidth: 200 },
											]}
											items={result.walletMatches}
											empty={renderEmptyMatches("No direct withdrawal wallet matches found")}
										/>
									),
								},
							]}
						/>
					</SpaceBetween>
				</>
			) : null}
		</SpaceBetween>
	);
}

export default ReferralCheck;
